import { queryOptions, useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { api } from '@/lib/api-client';

const HealthCheckResponseSchema = z.object({
  it: z.string(),
});

export type HealthCheckResponse = z.infer<typeof HealthCheckResponseSchema>;

/**
 * Check API health status.
 *
 * @returns Health check response from the API.
 */
export const checkHealth = async () => {
  const result = await api.get<HealthCheckResponse>('health_check');
  return HealthCheckResponseSchema.parse(result.data);
};

/**
 * Get query options for checking API health.
 *
 * @returns Query options for checking API health.
 */
export const checkHealthQueryOptions = () => {
  return queryOptions({
    queryKey: ['health-check'],
    queryFn: checkHealth,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    retry: Infinity,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 15_000),
  });
};

/**
 * Check API health status.
 *
 * @returns Query result for API health status.
 */
export const useHealthCheckQuery = () => {
  return useQuery(checkHealthQueryOptions());
};
